import { useQuery } from '@tanstack/react-query';
import hash from 'object-hash';
import { startOfDay } from 'date-fns';
import { useSuperUser } from '../../api.context';
import {
    availabilityProductDtoToAvailabilityProduct,
    filterToRequestBodyConverter,
} from './availability.converters';

// @ts-ignore-next-line
// eslint-disable-next-line
import Worker from './availability.worker.js?worker';

import {
    Availability,
    AvailabilityFilterValues,
    AvailabilityProduct,
    AvailableProduct,
    AvailableProductOption,
} from './availability.types';
import {
    PaxData,
    ProductOptionType,
    ProductsService,
    TimezoneName,
} from '../../generated';
import { CustomQueryOptions } from '../common.types';
import { ComputeAvailabilityResDto } from '../../other/ComputeAvailabilityResDto';

async function communicateWithWorker(data: any) {
    return new Promise<Availability[] | undefined>(resolve => {
        const worker = new Worker();

        // eslint-disable-next-line
        worker.onmessage = function (event: any) {
            resolve(event.data);
            worker.terminate();
        };

        // eslint-disable-next-line
        worker.onerror = function (error: any) {
            // eslint-disable-next-line
            console.log(error);
            resolve(undefined);
            worker.terminate();
        };

        worker.postMessage(data);
    });
}

export const useAvailability = (
    params: {
        requestBody: AvailabilityFilterValues;
    },
    options?: CustomQueryOptions<{
        products: Record<string, AvailableProduct>;
        events?: Availability[];
    }>
) => {
    const superUser = useSuperUser();

    return useQuery({
        queryKey: [params?.requestBody],
        queryFn: async () => {
            const products: Record<string, AvailableProduct> = {};

            const { data } = await ProductsService.computeAvailability({
                body: filterToRequestBodyConverter(params?.requestBody),
                query: superUser ? { superuser: superUser } : {},
            });

            const allEvents = (data as ComputeAvailabilityResDto).items?.map(
                ({ events, ...item }) => {
                    const product =
                        availabilityProductDtoToAvailabilityProduct(item);
                    const key = hash(product);
                    products[key] = product;

                    return {
                        key,
                        id: product.id,
                        name: product.name,
                        type: product.type,
                        discount: product.discount,
                        shared: product.shared,
                        events: [
                            ...(events?.new || []),
                            ...(events?.shared.filter(n => n.optimal) || []),
                        ],
                    };
                }
            );

            const events = await communicateWithWorker(allEvents || []);

            return {
                products,
                events,
            };
        },
        ...options,
    });
};

interface UseDayAvailabilityParams {
    time: number;
    capacityId?: string;
    pax: PaxData;
    productId: string;
    options?: AvailableProductOption[];
}

const useDayAvailabilityKey = 'useDayAvailabilityKey';

export const useDayAvailability = (
    params: UseDayAvailabilityParams,
    defaultValue: {
        products?: Record<string, AvailableProduct>;
        availableProduct: AvailabilityProduct;
    },
    options?: CustomQueryOptions<{
        products?: Record<string, AvailableProduct>;
        availableProduct: AvailabilityProduct;
    }>
) => {
    const guideOptionIds = params.options
        ?.filter(o => o.id && o.type === ProductOptionType.GUIDE)
        .map(o => o.id as string);

    return useQuery({
        queryKey: [
            useDayAvailabilityKey,
            params.pax,
            params.time,
            params.productId,
            params.capacityId,
            guideOptionIds,
        ],
        queryFn: async () => {
            const products: Record<string, AvailableProduct> = {};

            if (!guideOptionIds?.length) return defaultValue;

            const day = startOfDay(new Date(params.time));
            const { data } = await ProductsService.computeAvailability({
                body: {
                    date: {
                        timezone: {
                            name: Intl.DateTimeFormat().resolvedOptions()
                                .timeZone as TimezoneName,
                        },
                        start: Math.round(day.getTime() / 1000),
                        end:
                            Math.round(day.getTime() / 1000) + 24 * 60 * 60 - 1,
                    },
                    pax: params.pax,
                    products: [
                        {
                            id: params.productId,
                            options: guideOptionIds?.map(id => ({ id })),
                            capacity: params.capacityId
                                ? { items: [params.capacityId] }
                                : undefined,
                        },
                    ],
                },
            });
            const allEvents = (
                data as unknown as ComputeAvailabilityResDto
            ).items?.map(({ events, ...item }) => {
                const product =
                    availabilityProductDtoToAvailabilityProduct(item);
                const key = hash(product);
                products[key] = product;

                return {
                    key,
                    id: product.id,
                    name: product.name,
                    type: product.type,
                    shared: product.shared,
                    events: [
                        ...(events?.new || []),
                        ...(events?.shared.filter(n => n.optimal) || []),
                    ],
                };
            });

            const events = await communicateWithWorker(allEvents || []);

            return {
                products,
                availableProduct: events?.length
                    ? events[0].products[0]
                    : { ...defaultValue.availableProduct, events: [] },
            };
        },
        ...options,
    });
};
